import { GatewayControleQuinquennal } from "@conformite/gateway";
import { LoadingPage, Button } from "@endya-conformite/ui-kit";
import Loader from "@endya-conformite/ui-kit/src/assets/loader.svg?react";
import { useIsQuestionnaireComplete } from "@src/components/questionnaire/QuestionnaireForm/useIsQuestionnaireComplete";
import { ConnectedPageTemplate } from "@src/components/templates/ConnectedPageTemplate";
import { PageWithTitleTemplate } from "@src/components/templates/PageWithTitleTemplate";
import { useCreateRemediationMutation } from "@src/data/controle-quinquennal/hook/useCreateRemediationMutation";
import { useUpdateRemediationMutation } from "@src/data/controle-quinquennal/hook/useUpdateRemediationMutation";
import { useCQAxeRemediationsQuery } from "@src/data/controle-quinquennal/hook/useCQAxeRemediationsQuery";
import { useCQWithAxesQuery } from "@src/data/controle-quinquennal/hook/useCQWithAxesQuery";
import { useEntreprise } from "@src/data/entreprise/hook/useEntreprise";
import { formatTitle } from "@src/helper/title.helper";
import {
  axeTypeToLabel,
  axeTypeToQuestionnaireSlug,
  CQParams,
  cqDossierUrl,
} from "@src/pages/controle-quinquennal/controleQuinquennal.helper";
import { useIsAssujettiAxeComplete } from "@src/pages/controle-quinquennal/useIsAssujettiAxeComplete";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FIL_ARIANE_MES_CONTROLES } from "@src/pages/controle-quinquennal/cqListe.wording";
import styles from "./CQRemediationPage.module.scss";
import {
  CQRemediationForm,
  CQRemediationFormValues,
} from "./components/CQRemediationForm";

export const REMEDIATION_TAB_READY = "remediation-tab-ready";
export const REMEDIATION_TAB_PAYLOAD = "remediation-tab-payload";
export const REMEDIATION_TAB_CREATED = "remediation-tab-created";

const FORM_ID = "cq-remediation-page-form";

function CQRemediationPageContent({ idEntreprise, idCQ, idAxe }: CQParams) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shouldTransfer = searchParams.get("transfer") === "1";
  const idRemediation = searchParams.get("idRemediation") ?? undefined;
  const isEdit = Boolean(idRemediation);
  const shouldFetchRemediation = isEdit && !shouldTransfer;

  const [initialValues, setInitialValues] = useState<
    CQRemediationFormValues | undefined
  >(
    shouldTransfer || shouldFetchRemediation
      ? undefined
      : { description: "", files: [] }
  );

  const { data: cq } = useCQWithAxesQuery(idEntreprise);
  const { data: entreprise } = useEntreprise(cq?.millesime, idEntreprise);
  const { data: remediations } = useCQAxeRemediationsQuery(idCQ, idAxe, {
    enabled: shouldFetchRemediation,
  });
  const createMutation = useCreateRemediationMutation(idEntreprise);
  const updateMutation = useUpdateRemediationMutation(idEntreprise);
  const isPending = isEdit
    ? updateMutation.isPending
    : createMutation.isPending;
  const axeTypeForSlug = cq?.controleQuinquennalAxes?.find(
    (axe) => axe.idAxe === idAxe
  )?.type;
  const isAssujettiAxe =
    axeTypeForSlug ===
    GatewayControleQuinquennal.ControleQuinquennalAxeType.ASSUJETTI;
  const questionnaireSlug = axeTypeForSlug
    ? axeTypeToQuestionnaireSlug[axeTypeForSlug]
    : undefined;
  const { isComplete: isQuestionnaireComplete } = useIsQuestionnaireComplete(
    questionnaireSlug,
    "CONTROLE_QUINQUENNAL_AXE",
    idAxe
  );
  const { isComplete: isAssujettiComplete, isEmpty: isAssujettiAxeEmpty } =
    useIsAssujettiAxeComplete({
      enabled: isAssujettiAxe,
      millesime: cq?.millesime,
      siren: entreprise?.siren,
      entreprise,
    });
  const isAxeComplete = isAssujettiAxe
    ? isAssujettiComplete || isAssujettiAxeEmpty
    : isQuestionnaireComplete;

  useEffect(() => {
    if (!shouldTransfer) return undefined;
    const opener = window.opener as Window | null;
    if (!opener) {
      setInitialValues({ description: "", files: [] });
      return undefined;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== opener) return;
      const data = event.data as
        { type?: string; payload?: CQRemediationFormValues } | null | undefined;
      if (!data || data.type !== REMEDIATION_TAB_PAYLOAD) return;
      const { payload } = data;
      setInitialValues({
        description: payload?.description ?? "",
        files: payload?.files ?? [],
      });
    };

    window.addEventListener("message", handleMessage);
    opener.postMessage({ type: REMEDIATION_TAB_READY }, window.location.origin);

    const fallbackTimeout = window.setTimeout(() => {
      setInitialValues((current) => current ?? { description: "", files: [] });
    }, 2000);

    return () => {
      window.removeEventListener("message", handleMessage);
      window.clearTimeout(fallbackTimeout);
    };
  }, [shouldTransfer]);

  useEffect(() => {
    if (!shouldFetchRemediation || !remediations) return;
    const remediation = remediations.find((r) => r.id === idRemediation);
    setInitialValues(
      remediation
        ? {
            description: remediation.description,
            files: remediation.files.map((file) => ({
              idFile: file.idFile,
              pathname: file.pathname,
              filename: file.filename,
            })),
          }
        : { description: "", files: [] }
    );
  }, [shouldFetchRemediation, remediations, idRemediation]);

  const backToAxe = () =>
    navigate(`/controle-quinquennal/${idEntreprise}/${idCQ}/${idAxe}`);
  const backToAxes = () => navigate(cqDossierUrl(idEntreprise, idCQ));

  const handleSubmit = async (values: CQRemediationFormValues) => {
    const filesToAdd = values.files.filter((f) => f.file);
    const idFiles = values.files
      .filter((f) => f.idFile && !f.file)
      .map((f) => f.idFile as string);

    if (isEdit && idRemediation) {
      await updateMutation.mutateAsync({
        idControleQuinquennal: idCQ,
        idAxe,
        idRemediation,
        payload: {
          description: values.description,
          idFiles,
        },
        files: filesToAdd.map((f) => f.file) as File[],
      });
    } else {
      await createMutation.mutateAsync({
        idControleQuinquennal: idCQ,
        idAxe,
        payload: {
          description: values.description,
          idFiles,
        },
        files: filesToAdd.map((f) => f.file) as File[],
      });
    }

    const opener = window.opener as Window | null;
    if (opener) {
      opener.postMessage(
        { type: REMEDIATION_TAB_CREATED, idEntreprise },
        window.location.origin
      );
      window.close();
      return;
    }
    backToAxes();
  };

  if (!entreprise || !cq || !initialValues)
    return (
      <ConnectedPageTemplate>
        <LoadingPage withText={false} />
      </ConnectedPageTemplate>
    );

  const axeType = cq.controleQuinquennalAxes?.find(
    (axe) => axe.idAxe === idAxe
  )?.type;

  const formattedTitle = formatTitle(entreprise.raisonSociale, cq.millesime);

  const breadcrumbElements = [
    FIL_ARIANE_MES_CONTROLES,
    {
      label: formattedTitle,
      value: cqDossierUrl(idEntreprise, idCQ),
    },
    {
      label: axeType ? axeTypeToLabel[axeType] : "",
      value: `/controle-quinquennal/${idEntreprise}/${idCQ}/${idAxe}`,
    },
    { label: "Commentaire de remédiation", value: "current" },
  ];

  return (
    <PageWithTitleTemplate
      title="Commentaire de remédiation"
      breadcrumb={{
        items: breadcrumbElements,
        setSelected: (nextRoute) => {
          if (nextRoute !== "current") navigate(nextRoute);
        },
        selected: breadcrumbElements.at(-1)?.value ?? "current",
      }}
    >
      <div className={styles.formContainer}>
        <CQRemediationForm
          formId={FORM_ID}
          onSubmit={handleSubmit}
          initialValues={initialValues}
        />
      </div>
      <div className={styles.footer}>
        <Button variant="outline" onClick={backToAxe}>
          Annuler
        </Button>
        <Button
          type="submit"
          form={FORM_ID}
          disabled={isPending || !isAxeComplete}
          icon={isPending ? <Loader /> : undefined}
        >
          Valider
        </Button>
      </div>
    </PageWithTitleTemplate>
  );
}

export function CQRemediationPage() {
  const { idEntreprise, idCQ, idAxe } = useParams<CQParams>();
  if (!idEntreprise || !idCQ || !idAxe)
    return <div>Impossible d&apos;afficher la page</div>;
  return (
    <CQRemediationPageContent
      idEntreprise={idEntreprise}
      idCQ={idCQ}
      idAxe={idAxe}
    />
  );
}
